import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDataRefresh, ENTITY_TYPES } from '../context/DataRefreshContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Search,
  Plus,
  Users,
  Phone,
  Mail,
  FileText,
  DollarSign,
  Eye,
  Edit,
  ChevronRight,
  User,
  CreditCard,
  Calendar,
  TrendingUp,
  Merge,
  AlertTriangle,
  X,
  Check,
  Trash2,
  UserCircle,
  MessageCircle,
  History,
  QrCode,
  Star,
  Gift
} from 'lucide-react';
import { StatusBadge } from '../components/ui/StatusBadge';
import { EmptyState } from '../components/ui/EmptyState';
import { ActionButton } from '../components/ui/ActionButton';
import AccesoMiJPMBotones from '../components/mijpm/AccesoMiJPMBotones';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPOS_DOCUMENTO = [
  { value: 'CC', label: 'Cédula de Ciudadanía' },
  { value: 'CE', label: 'Cédula de Extranjería' },
  { value: 'NIT', label: 'NIT' },
  { value: 'PASAPORTE', label: 'Pasaporte' },
  { value: 'TI', label: 'Tarjeta de Identidad' },
  { value: 'OTRO', label: 'Otro' }
];

const formatPrice = (value) => {
  if (!value) return '$0';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ClientesList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifyDeletion, refreshTimestamps } = useDataRefresh();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Modal crear cliente
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [nuevoCliente, setNuevoCliente] = useState({
    tipo_documento: 'CC',
    numero_documento: '',
    nombre: '',
    telefono: '',
    correo: '',
    direccion: '',
    ciudad: ''
  });
  const [creando, setCreando] = useState(false);

  // Modal fusionar clientes
  const [showFusionarModal, setShowFusionarModal] = useState(false);
  const [clienteParaFusionar, setClienteParaFusionar] = useState(null);
  const [clienteDestino, setClienteDestino] = useState('');
  const [fusionando, setFusionando] = useState(false);

  // Modal agregar puntos manualmente
  const [showPuntosModal, setShowPuntosModal] = useState(false);
  const [clienteParaPuntos, setClienteParaPuntos] = useState(null);
  const [puntosData, setPuntosData] = useState({
    puntos: '',
    motivo: 'Referido',
    observaciones: ''
  });
  const [asignandoPuntos, setAsignandoPuntos] = useState(false);
  const [saldoActual, setSaldoActual] = useState(null);

  // Modal eliminar cliente (borrado lógico)
  const [showEliminarModal, setShowEliminarModal] = useState(false);
  const [clienteParaEliminar, setClienteParaEliminar] = useState(null);
  const [dependenciasCliente, setDependenciasCliente] = useState(null);
  const [cargandoDependencias, setCargandoDependencias] = useState(false);
  const [motivoEliminacion, setMotivoEliminacion] = useState('');
  const [confirmacionNombre, setConfirmacionNombre] = useState('');
  const [eliminando, setEliminando] = useState(false);

  // Motivos predefinidos para agregar puntos
  const MOTIVOS_PUNTOS = [
    'Referido',
    'Promoción especial',
    'Compensación por servicio',
    'Bonificación por fidelidad',
    'Ajuste administrativo',
    'Otro'
  ];

  const fetchClientes = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/clientes`, {
        params: { search, page, limit: 20 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientes(response.data.clientes || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  // Refrescar datos cuando otra vista invalide el caché de clientes
  useEffect(() => {
    fetchClientes();
  }, [refreshTimestamps.clientes]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchClientes();
  };

  const crearCliente = async () => {
    if (!nuevoCliente.numero_documento || !nuevoCliente.nombre) {
      toast.error('Documento y nombre son obligatorios');
      return;
    }
    
    setCreando(true);
    try {
      const token = localStorage.getItem('token');
      // Clean empty strings to null for optional fields
      const clienteData = {
        tipo_documento: nuevoCliente.tipo_documento,
        numero_documento: nuevoCliente.numero_documento,
        nombre: nuevoCliente.nombre,
        telefono: nuevoCliente.telefono || null,
        correo: nuevoCliente.correo || null,
        direccion: nuevoCliente.direccion || null,
        ciudad: nuevoCliente.ciudad || null
      };
      const response = await axios.post(`${API_URL}/api/clientes`, clienteData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Cliente ${response.data.id_cliente} creado exitosamente`);
      setShowCrearModal(false);
      setNuevoCliente({
        tipo_documento: 'CC',
        numero_documento: '',
        nombre: '',
        telefono: '',
        correo: '',
        direccion: '',
        ciudad: ''
      });
      fetchClientes();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Error al crear cliente';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setCreando(false);
    }
  };

  const fusionarClientes = async () => {
    if (!clienteParaFusionar || !clienteDestino) {
      toast.error('Seleccione el cliente destino');
      return;
    }
    
    setFusionando(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/clientes/fusionar`, {
        cliente_principal_id: clienteDestino,
        cliente_secundario_id: clienteParaFusionar.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setShowFusionarModal(false);
      setClienteParaFusionar(null);
      setClienteDestino('');
      fetchClientes();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Error al fusionar clientes';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setFusionando(false);
    }
  };

  const eliminarCliente = async (cliente) => {
    // Abrir modal y cargar dependencias
    setClienteParaEliminar(cliente);
    setMotivoEliminacion('');
    setConfirmacionNombre('');
    setDependenciasCliente(null);
    setCargandoDependencias(true);
    setShowEliminarModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/gerencia/cliente/${cliente.id}/analisis-eliminacion`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDependenciasCliente(response.data);
    } catch (error) {
      console.error('Error cargando dependencias:', error);
      toast.error('Error al verificar dependencias del cliente');
      setShowEliminarModal(false);
    } finally {
      setCargandoDependencias(false);
    }
  };

  const confirmarEliminacionCliente = async () => {
    if (!clienteParaEliminar) return;
    
    if (!motivoEliminacion.trim() || motivoEliminacion.length < 10) {
      toast.error('El motivo debe tener al menos 10 caracteres');
      return;
    }
    
    if (confirmacionNombre.trim().toLowerCase() !== clienteParaEliminar.nombre.toLowerCase()) {
      toast.error('El nombre de confirmación no coincide');
      return;
    }
    
    setEliminando(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/gerencia/cliente/${clienteParaEliminar.id}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {
          motivo: motivoEliminacion,
          confirmar_nombre: confirmacionNombre
        }
      });
      toast.success('Cliente eliminado lógicamente. Puede restaurarse desde Registros Eliminados.');
      setShowEliminarModal(false);
      setClienteParaEliminar(null);
      
      // Notificar invalidación de caché para actualizar todas las vistas
      notifyDeletion(ENTITY_TYPES.CLIENTES, { cliente_id: clienteParaEliminar.id });
      
      fetchClientes();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Error al eliminar cliente');
    } finally {
      setEliminando(false);
    }
  };

  // Función para abrir modal de agregar puntos
  const abrirModalPuntos = async (cliente) => {
    setClienteParaPuntos(cliente);
    setPuntosData({ puntos: '', motivo: 'Referido', observaciones: '' });
    setSaldoActual(null);
    setShowPuntosModal(true);
    
    // Obtener saldo actual de puntos
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/puntos-jpm/cliente/${cliente.id}/saldo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSaldoActual(response.data.saldo_puntos || 0);
    } catch (error) {
      console.error('Error al obtener saldo de puntos:', error);
      setSaldoActual(0);
    }
  };

  // Función para asignar puntos manualmente
  const asignarPuntos = async () => {
    const cantidadPuntos = parseInt(puntosData.puntos);
    
    if (!cantidadPuntos || cantidadPuntos <= 0) {
      toast.error('La cantidad de puntos debe ser un número positivo');
      return;
    }
    
    if (!puntosData.motivo || puntosData.motivo.trim() === '') {
      toast.error('El motivo es obligatorio');
      return;
    }
    
    setAsignandoPuntos(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/puntos-jpm/ajuste-administrativo`, {
        cliente_id: clienteParaPuntos.id,
        puntos: cantidadPuntos,
        motivo: puntosData.motivo,
        observaciones: puntosData.observaciones || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`✅ ${cantidadPuntos} puntos asignados a ${clienteParaPuntos.nombre}. Nuevo saldo: ${response.data.nuevo_saldo} puntos`);
      setShowPuntosModal(false);
      setClienteParaPuntos(null);
      setPuntosData({ puntos: '', motivo: 'Referido', observaciones: '' });
      fetchClientes(); // Refrescar lista
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Error al asignar puntos';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (detail?.mensaje) {
        errorMessage = detail.mensaje;
      }
      toast.error(errorMessage);
    } finally {
      setAsignandoPuntos(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header Fijo con Safe Area para iOS */}
      <header className="fixed top-0 left-0 w-full z-50 bg-zinc-900/95 backdrop-blur-xl border-b border-zinc-800 shadow-lg safe-area-header">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          {/* Mobile: 2 rows */}
          <div className="flex flex-col gap-3 md:hidden">
            {/* Row 1: Back + Title */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors flex-shrink-0"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <UserCircle className="w-4 h-4 text-purple-400" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg font-serif truncate">Clientes</h1>
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wider">{total} registrados</p>
                </div>
              </div>
            </div>
            {/* Row 2: Actions */}
            <ActionButton
              onClick={() => setShowCrearModal(true)}
              variant="gold"
              icon={Plus}
              className="w-full justify-center"
              data-testid="crear-cliente-btn-mobile"
            >
              Nuevo Cliente
            </ActionButton>
          </div>
          
          {/* Desktop: Single row */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <UserCircle className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h1 className="text-xl font-serif">Clientes</h1>
                  <p className="text-xs text-zinc-400 uppercase tracking-wider">{total} {total === 1 ? 'cliente registrado' : 'clientes registrados'}</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/qr-autorregistro')}
                className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                title="Ver QR de Autorregistro"
                data-testid="qr-autorregistro-btn"
              >
                <QrCode className="w-4 h-4" />
                <span className="hidden lg:inline">QR Registro</span>
              </button>
              
              <ActionButton
                onClick={() => setShowCrearModal(true)}
                variant="gold"
                icon={Plus}
                data-testid="crear-cliente-btn"
              >
                Nuevo Cliente
              </ActionButton>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 content-below-safe-header">
        {/* Buscador */}
        <div className="mb-6 p-5 bg-zinc-900 rounded-xl border border-zinc-800 border-l-4 border-l-purple-500">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre, cédula, teléfono, correo o ID..."
                className="w-full pl-12 pr-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none transition-all"
                data-testid="search-clientes"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-zinc-700 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-zinc-400" />
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Lista de clientes */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-10 h-10 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
          </div>
        ) : clientes.length === 0 ? (
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 border-l-4 border-l-purple-500 p-8">
            <EmptyState
              type="clientes"
              icon={Users}
              title={search ? 'No se encontraron clientes' : 'No hay clientes registrados'}
              description={search ? 'Intenta con otros términos de búsqueda' : 'Registra tu primer cliente para comenzar'}
              actionLabel={search ? 'Limpiar búsqueda' : 'Registrar cliente'}
              onAction={search ? () => setSearch('') : () => setShowCrearModal(true)}
            />
          </div>
        ) : (
          <>
            {/* Contador de resultados */}
            <div className="flex items-center justify-between px-1 mb-4">
              <p className="text-sm text-zinc-400">
                Mostrando <span className="font-medium text-white">{clientes.length}</span> de {total} clientes
                {search && <span className="text-purple-400"> para "{search}"</span>}
              </p>
            </div>
            
            <div className="space-y-3">
            {clientes.map(cliente => (
              <div
                key={cliente.id}
                className="bg-zinc-900 border border-zinc-800 border-l-4 border-l-purple-500 rounded-xl p-5 hover:border-zinc-700 hover:shadow-lg transition-all"
                data-testid={`cliente-card-${cliente.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <User className="w-6 h-6 text-purple-400" />
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-serif text-lg text-white">{cliente.nombre}</h3>
                        <span className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-400 rounded-full uppercase tracking-wider font-medium">
                          {cliente.id_cliente}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-2 text-sm text-zinc-400 flex-wrap">
                        <span className="flex items-center gap-1.5">
                          <CreditCard className="w-3.5 h-3.5 text-zinc-500" />
                          {cliente.tipo_documento} {cliente.numero_documento}
                        </span>
                        {cliente.telefono && (
                          <span className="flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5 text-zinc-500" />
                            {cliente.telefono}
                          </span>
                        )}
                        {cliente.correo && (
                          <span className="flex items-center gap-1.5">
                            <Mail className="w-3.5 h-3.5 text-zinc-500" />
                            {cliente.correo}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Registrado: {formatDate(cliente.created_at)}
                        </span>
                        {cliente.origen === 'Autorregistro cliente' && (
                          <span className="flex items-center gap-1 text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                            <History className="w-3 h-3" />
                            Autorregistro
                          </span>
                        )}
                        {cliente.historial_autorregistros && cliente.historial_autorregistros.length > 0 && (
                          <span className="text-amber-400">
                            {cliente.historial_autorregistros.length} registro(s)
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Botón WhatsApp */}
                    {cliente.telefono && (
                      <a
                        href={`https://wa.me/${cliente.telefono.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola, ${cliente.nombre.split(' ')[0]}. Te saludamos de Juan Pablo Murillo.${cliente.historial_autorregistros && cliente.historial_autorregistros.length > 0 ? ` Recibimos tu registro y vimos que estás interesado(a) en ${cliente.historial_autorregistros[cliente.historial_autorregistros.length - 1]?.prendas?.join(', ') || 'nuestros productos'}. Queremos ayudarte con tu asesoría.` : ' Queremos continuar con tu asesoría.'}`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
                        title="Contactar por WhatsApp"
                        data-testid={`whatsapp-${cliente.id}`}
                      >
                        <MessageCircle className="w-4 h-4" />
                        <span className="hidden sm:inline">WhatsApp</span>
                      </a>
                    )}
                    
                    {/* Acceso a Mi JPM - Solo Gerencia */}
                    {user?.role === 'gerencia' && (
                      <AccesoMiJPMBotones
                        cliente={cliente}
                        onCreado={(cid) => setClientes(prev => prev.map(c => (c.id === cid ? { ...c, mi_jpm: { ...(c.mi_jpm || {}), habilitado: true } } : c)))}
                      />
                    )}
                    
                    {/* Botón Agregar Puntos - Solo Gerencia/Admin */}
                    {(user?.role === 'gerencia' || user?.role === 'admin') && (
                      <button
                        onClick={() => abrirModalPuntos(cliente)}
                        className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
                        title="Agregar puntos JPM manualmente"
                        data-testid={`agregar-puntos-${cliente.id}`}
                      >
                        <Star className="w-4 h-4" />
                        <span className="hidden sm:inline">Puntos</span>
                      </button>
                    )}
                    
                    <ActionButton
                      onClick={() => navigate(`/clientes/${cliente.id}`)}
                      variant="secondary"
                      size="sm"
                      icon={Eye}
                      className="bg-zinc-800 hover:bg-zinc-700 border-zinc-700 text-white"
                      data-testid={`ver-cliente-${cliente.id}`}
                    >
                      Ver
                    </ActionButton>
                    
                    {(user?.role === 'gerencia' || user?.role === 'admin') && (
                      <>
                        <button
                          onClick={() => {
                            setClienteParaFusionar(cliente);
                            setShowFusionarModal(true);
                          }}
                          className="p-2 hover:bg-amber-900/30 rounded-lg transition-colors"
                          title="Fusionar con otro cliente"
                        >
                          <Merge className="w-4 h-4 text-amber-500" />
                        </button>
                        <button
                          onClick={() => eliminarCliente(cliente)}
                          className="p-2 hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Eliminar cliente"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
            </div>
          </>
        )}

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
            >
              Anterior
            </button>
            <span className="px-4 py-2 text-zinc-400">
              Página {page} de {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
            >
              Siguiente
            </button>
          </div>
        )}
      </main>

      {/* Modal Crear Cliente */}
      {showCrearModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Plus className="w-5 h-5 text-[#D4AF37]" />
                  Nuevo Cliente
                </h2>
                <button
                  onClick={() => setShowCrearModal(false)}
                  className="p-2 hover:bg-zinc-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Tipo Documento *</label>
                  <select
                    value={nuevoCliente.tipo_documento}
                    onChange={(e) => setNuevoCliente(prev => ({ ...prev, tipo_documento: e.target.value }))}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                  >
                    {TIPOS_DOCUMENTO.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Número Documento *</label>
                  <input
                    type="text"
                    value={nuevoCliente.numero_documento}
                    onChange={(e) => setNuevoCliente(prev => ({ ...prev, numero_documento: e.target.value }))}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                    placeholder="1234567890"
                    data-testid="input-documento"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Nombre Completo *</label>
                <input
                  type="text"
                  value={nuevoCliente.nombre}
                  onChange={(e) => setNuevoCliente(prev => ({ ...prev, nombre: e.target.value }))}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                  placeholder="Juan Pérez García"
                  data-testid="input-nombre"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    value={nuevoCliente.telefono}
                    onChange={(e) => setNuevoCliente(prev => ({ ...prev, telefono: e.target.value }))}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                    placeholder="3001234567"
                    data-testid="input-telefono"
                  />
                </div>
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Correo</label>
                  <input
                    type="email"
                    value={nuevoCliente.correo}
                    onChange={(e) => setNuevoCliente(prev => ({ ...prev, correo: e.target.value }))}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                    placeholder="correo@ejemplo.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Dirección</label>
                <input
                  type="text"
                  value={nuevoCliente.direccion}
                  onChange={(e) => setNuevoCliente(prev => ({ ...prev, direccion: e.target.value }))}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                  placeholder="Calle 123 #45-67"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Ciudad</label>
                <input
                  type="text"
                  value={nuevoCliente.ciudad}
                  onChange={(e) => setNuevoCliente(prev => ({ ...prev, ciudad: e.target.value }))}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-[#D4AF37] focus:outline-none"
                  placeholder="Bogotá"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <button
                onClick={() => setShowCrearModal(false)}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={crearCliente}
                disabled={creando}
                className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-medium rounded-lg disabled:opacity-50"
                data-testid="confirmar-crear-cliente"
              >
                {creando ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black"></div>
                    Creando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Crear Cliente
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Fusionar Clientes */}
      {showFusionarModal && clienteParaFusionar && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Merge className="w-5 h-5 text-amber-500" />
                  Fusionar Clientes
                </h2>
                <button
                  onClick={() => {
                    setShowFusionarModal(false);
                    setClienteParaFusionar(null);
                    setClienteDestino('');
                  }}
                  className="p-2 hover:bg-zinc-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-400">Esta acción no se puede deshacer</p>
                    <p className="text-zinc-300 mt-1">
                      Las fichas y cotizaciones del cliente seleccionado se moverán al cliente destino. 
                      El cliente seleccionado será eliminado.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-zinc-800 rounded-lg p-4">
                <p className="text-xs text-zinc-400 mb-2">Cliente a fusionar (será eliminado):</p>
                <p className="font-medium">{clienteParaFusionar.nombre}</p>
                <p className="text-sm text-zinc-400">
                  {clienteParaFusionar.tipo_documento} {clienteParaFusionar.numero_documento}
                </p>
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">
                  Seleccione el cliente destino (se conservará):
                </label>
                <select
                  value={clienteDestino}
                  onChange={(e) => setClienteDestino(e.target.value)}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-amber-500 focus:outline-none"
                >
                  <option value="">-- Seleccionar cliente --</option>
                  {clientes
                    .filter(c => c.id !== clienteParaFusionar.id)
                    .map(c => (
                      <option key={c.id} value={c.id}>
                        {c.nombre} ({c.tipo_documento} {c.numero_documento})
                      </option>
                    ))}
                </select>
              </div>
            </div>
            
            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowFusionarModal(false);
                  setClienteParaFusionar(null);
                  setClienteDestino('');
                }}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={fusionarClientes}
                disabled={fusionando || !clienteDestino}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg disabled:opacity-50"
              >
                {fusionando ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Fusionando...
                  </>
                ) : (
                  <>
                    <Merge className="w-4 h-4" />
                    Fusionar Clientes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Agregar Puntos Manualmente */}
      {showPuntosModal && clienteParaPuntos && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800 bg-gradient-to-r from-purple-900/30 to-zinc-900">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Star className="w-5 h-5 text-purple-400" />
                  Agregar Puntos JPM
                </h2>
                <button
                  onClick={() => {
                    setShowPuntosModal(false);
                    setClienteParaPuntos(null);
                    setPuntosData({ puntos: '', motivo: 'Referido', observaciones: '' });
                  }}
                  className="p-2 hover:bg-zinc-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-5">
              {/* Info del cliente */}
              <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-white">{clienteParaPuntos.nombre}</p>
                    <p className="text-sm text-zinc-400">{clienteParaPuntos.id_cliente} • {clienteParaPuntos.tipo_documento} {clienteParaPuntos.numero_documento}</p>
                  </div>
                </div>
                {saldoActual !== null && (
                  <div className="mt-3 pt-3 border-t border-zinc-700 flex items-center justify-between">
                    <span className="text-sm text-zinc-400">Saldo actual de puntos:</span>
                    <span className="font-bold text-purple-400 text-lg">{saldoActual.toLocaleString()} puntos</span>
                  </div>
                )}
              </div>
              
              {/* Cantidad de puntos */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Cantidad de Puntos a Agregar *
                </label>
                <input
                  type="number"
                  min="1"
                  value={puntosData.puntos}
                  onChange={(e) => setPuntosData(prev => ({ ...prev, puntos: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 focus:outline-none text-lg"
                  placeholder="Ej: 100"
                  data-testid="input-puntos-cantidad"
                />
                {puntosData.puntos && parseInt(puntosData.puntos) > 0 && (
                  <p className="text-xs text-purple-400 mt-2">
                    💡 Equivale a {formatPrice(parseInt(puntosData.puntos) * 100)} en beneficios para el cliente
                  </p>
                )}
              </div>
              
              {/* Motivo */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Motivo *
                </label>
                <select
                  value={puntosData.motivo}
                  onChange={(e) => setPuntosData(prev => ({ ...prev, motivo: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-purple-500 focus:outline-none"
                  data-testid="select-puntos-motivo"
                >
                  {MOTIVOS_PUNTOS.map(motivo => (
                    <option key={motivo} value={motivo}>{motivo}</option>
                  ))}
                </select>
              </div>
              
              {/* Observaciones */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">
                  Observaciones (opcional)
                </label>
                <textarea
                  value={puntosData.observaciones}
                  onChange={(e) => setPuntosData(prev => ({ ...prev, observaciones: e.target.value }))}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-purple-500 focus:outline-none resize-none"
                  placeholder="Ej: Referido por el cliente Juan Pérez"
                  rows={3}
                  data-testid="input-puntos-observaciones"
                />
              </div>
              
              {/* Aviso */}
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Gift className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-purple-300">Asignación Manual de Puntos</p>
                    <p className="text-zinc-400 mt-1">
                      Los puntos asignados quedarán registrados en el historial del cliente con tu nombre de usuario, la fecha y el motivo indicado.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPuntosModal(false);
                  setClienteParaPuntos(null);
                  setPuntosData({ puntos: '', motivo: 'Referido', observaciones: '' });
                }}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={asignarPuntos}
                disabled={asignandoPuntos || !puntosData.puntos || parseInt(puntosData.puntos) <= 0}
                className="flex items-center gap-2 px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirmar-asignar-puntos"
              >
                {asignandoPuntos ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Asignando...
                  </>
                ) : (
                  <>
                    <Star className="w-4 h-4" />
                    Asignar Puntos
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Eliminar Cliente (Borrado Lógico) */}
      {showEliminarModal && clienteParaEliminar && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2 text-red-400">
                  <Trash2 className="w-5 h-5" />
                  Eliminar Cliente
                </h2>
                <button
                  onClick={() => {
                    setShowEliminarModal(false);
                    setClienteParaEliminar(null);
                  }}
                  className="p-2 hover:bg-zinc-800 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Info del cliente */}
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <p className="text-sm text-zinc-400">Cliente a eliminar</p>
                <p className="font-semibold text-lg">{clienteParaEliminar.nombre}</p>
                <p className="text-sm text-zinc-500">{clienteParaEliminar.id_cliente}</p>
              </div>
              
              {/* Dependencias */}
              {cargandoDependencias ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#D4AF37]"></div>
                  <span className="ml-2 text-zinc-400">Verificando dependencias...</span>
                </div>
              ) : dependenciasCliente && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-zinc-300 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Registros relacionados que serán afectados:
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                      <p className="text-2xl font-bold text-amber-400">{dependenciasCliente.cotizaciones_count}</p>
                      <p className="text-xs text-zinc-400">Cotizaciones</p>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                      <p className="text-2xl font-bold text-green-400">{dependenciasCliente.ventas_count}</p>
                      <p className="text-xs text-zinc-400">Ventas</p>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                      <p className="text-2xl font-bold text-blue-400">{dependenciasCliente.pagos_count}</p>
                      <p className="text-xs text-zinc-400">Pagos</p>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                      <p className="text-2xl font-bold text-purple-400">{dependenciasCliente.puntos_movimientos}</p>
                      <p className="text-xs text-zinc-400">Mov. Puntos</p>
                    </div>
                  </div>
                  
                  {dependenciasCliente.saldo_puntos > 0 && (
                    <div className="bg-purple-500/20 border border-purple-500/40 rounded-lg p-3 flex items-center gap-2">
                      <Star className="w-5 h-5 text-purple-400" />
                      <span className="text-sm">
                        <strong>{dependenciasCliente.saldo_puntos}</strong> puntos JPM acumulados
                      </span>
                    </div>
                  )}
                  
                  {!dependenciasCliente.puede_eliminar && (
                    <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3">
                      <p className="text-sm text-red-300 font-medium">
                        {dependenciasCliente.razon_bloqueo}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Formulario de eliminación */}
              {dependenciasCliente?.puede_eliminar && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Motivo de eliminación * (mín. 10 caracteres)
                    </label>
                    <textarea
                      value={motivoEliminacion}
                      onChange={(e) => setMotivoEliminacion(e.target.value)}
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-red-500 focus:outline-none resize-none"
                      placeholder="Explique el motivo de la eliminación..."
                      rows={3}
                      data-testid="motivo-eliminacion"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      Escriba "{clienteParaEliminar.nombre}" para confirmar
                    </label>
                    <input
                      type="text"
                      value={confirmacionNombre}
                      onChange={(e) => setConfirmacionNombre(e.target.value)}
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-red-500 focus:outline-none"
                      placeholder="Escriba el nombre del cliente"
                      data-testid="confirmacion-nombre"
                    />
                  </div>
                  
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
                    <p className="text-xs text-zinc-400 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Este es un <strong className="text-amber-400">borrado lógico</strong>. El registro puede restaurarse desde "Registros Eliminados".
                    </p>
                  </div>
                </>
              )}
            </div>
            
            <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEliminarModal(false);
                  setClienteParaEliminar(null);
                }}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg"
              >
                Cancelar
              </button>
              {dependenciasCliente?.puede_eliminar && (
                <button
                  onClick={confirmarEliminacionCliente}
                  disabled={eliminando || motivoEliminacion.length < 10 || confirmacionNombre.trim().toLowerCase() !== clienteParaEliminar.nombre.toLowerCase()}
                  className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="confirmar-eliminar-cliente"
                >
                  {eliminando ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Eliminar Cliente
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientesList;
